import { Layers, MapIcon, SlidersHorizontal } from "lucide-react";
import React, { createContext, useContext, useMemo, useState } from "react";
import { useMapEditorProps } from "../../contexts/MapEditorContext";
import { LayersPanel } from "../panels/LayersPanel";
import { ShapePanel } from "../panels/ShapePanel/ShapePanel";
import { ZonesPanel } from "../panels/ZonesPanel";

export type EditorSide = "left" | "right";

export interface EditorPanelComponentProps {
  side: EditorSide;
}

export interface PanelConfig {
  id: string;
  title: string;
  icon: React.FC<{ size?: number | string; className?: string }>;
  component: React.ComponentType<EditorPanelComponentProps>;
}

export interface LayoutConfig {
  left: PanelConfig[];
  right: PanelConfig[];
}

interface PanelLayoutContextType {
  layoutConfig: LayoutConfig;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  setSideCollapsed: (side: EditorSide, collapsed: boolean) => void;
}

const PanelLayoutContext = createContext<PanelLayoutContextType | null>(null);

export function PanelLayoutProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const { restrictedMode } = useMapEditorProps();

  // Layers on the left, the shape inspector on the right — the classic
  // design-tool arrangement. Layers is dropped entirely in restricted
  // mode — a single-desk flow has nothing worth managing there.
  const layoutConfig: LayoutConfig = useMemo(
    () => ({
      left: restrictedMode
        ? []
        : [
            {
              id: "layers",
              title: "Layers",
              icon: Layers,
              component: LayersPanel
            },
            {
              id: "zones",
              title: "Zones",
              icon: MapIcon,
              component: ZonesPanel
            }
          ],
      right: [
        {
          id: "shape",
          title: "Inspector",
          icon: SlidersHorizontal,
          component: ShapePanel
        }
      ]
    }),
    [restrictedMode]
  );

  const setSideCollapsed = (side: EditorSide, collapsed: boolean) => {
    if (side === "left") setLeftCollapsed(collapsed);
    else setRightCollapsed(collapsed);
  };

  return (
    <PanelLayoutContext.Provider
      value={{
        layoutConfig,
        leftCollapsed,
        rightCollapsed,
        setSideCollapsed
      }}
    >
      {children}
    </PanelLayoutContext.Provider>
  );
}

export function usePanelLayout() {
  const context = useContext(PanelLayoutContext);
  if (!context) {
    throw new Error("usePanelLayout must be used within a PanelLayoutProvider");
  }
  return context;
}
